import {
  ABILITY_CHARGE_CONFIGS, abilityChargeConfig, checkChargeSpend, createInitialChargeState,
  syncChargeState, type AbilityChargeConfig, type AbilityChargeState,
} from '../../../shared/combat/abilityCharges';
import type { AbilityChargeSnapshot, ChargeWireState } from '../../../shared/combat/AbilityChargeWire';

interface PredictedSpend { abilityId: string; at: number; sequence: number }

/** Resource transactions outlive their visual dash. Only an atomic owner
 * snapshot retires them; movement confirmations never invent charge credits. */
export class AbilityChargePrediction {
  private base: Record<string, AbilityChargeState> = {};
  private pending = new Map<number, PredictedSpend>();
  private sessionId: string | null = null;
  private characterId = '';
  private lifeId = 0;
  private revision = 0;
  private localSequence = -1;

  beginOwner(sessionId: string | null, characterId = ''): void {
    if (this.sessionId === sessionId && (!characterId || this.characterId === characterId)) return;
    this.clear();
    this.sessionId = sessionId;
    this.characterId = characterId;
  }

  clear(): void {
    this.base = {};
    this.pending.clear();
    this.sessionId = null;
    this.characterId = '';
    this.lifeId = 0;
    this.revision = 0;
  }

  resetPredictions(): void { this.pending.clear(); }

  init(abilityId: string, config?: AbilityChargeConfig): AbilityChargeState {
    const cfg = config ?? abilityChargeConfig(abilityId);
    if (!cfg) throw new Error(`Unknown charge config for ${abilityId}`);
    this.base[abilityId] ??= createInitialChargeState(cfg);
    return this.get(abilityId)!;
  }

  get(abilityId: string, now = Date.now()): AbilityChargeState | undefined {
    const config = abilityChargeConfig(abilityId);
    if (!config) return undefined;
    this.base[abilityId] ??= createInitialChargeState(config);
    const projected = { ...this.base[abilityId] };
    // Replay prediction against a copy, preserving serial recovery and recast
    // timestamps without changing the authoritative base on each UI read.
    for (const spend of this.pending.values()) {
      if (spend.abilityId !== abilityId) continue;
      syncChargeState(projected, spend.at);
      projected.currentCharges = Math.max(0, projected.currentCharges - (config.consumePerCast ?? 1));
      projected.lastCastAt = Math.max(projected.lastCastAt ?? spend.at, spend.at);
      projected.rechargeStartedAt ??= spend.at;
    }
    syncChargeState(projected, now);
    return projected;
  }

  canSpend(abilityId: string, now = Date.now()) {
    const config = abilityChargeConfig(abilityId);
    if (!config) return { ok: true };
    return checkChargeSpend(this.get(abilityId, now)!, config, now);
  }

  consume(abilityId: string, now = Date.now(), sequence?: number): boolean {
    const config = abilityChargeConfig(abilityId);
    if (!config) return true;
    if (sequence !== undefined && (!Number.isSafeInteger(sequence) || sequence <= 0 || this.pending.has(sequence))) return false;
    if (!this.canSpend(abilityId, now).ok) return false;
    const key = sequence ?? this.localSequence--;
    this.pending.set(key, { abilityId, at: now, sequence: key });
    return true;
  }

  /** Local-only rollback; wire outcomes always use applySnapshot instead. */
  refund(abilityId: string, sequence?: number): boolean {
    const spend = sequence === undefined
      ? [...this.pending.values()].find(value => value.abilityId === abilityId)
      : this.pending.get(sequence);
    if (!spend || spend.abilityId !== abilityId) return false;
    return this.pending.delete(spend.sequence);
  }

  sync(abilityId: string, state: ChargeWireState): void {
    const config = abilityChargeConfig(abilityId);
    if (!config || !validState(state, config.maxCharges)) return;
    this.base[abilityId] = { ...state, maxCharges: config.maxCharges, rechargeTimeMs: config.rechargeTimeMs };
  }

  /** Same-owner refreshes retain unacknowledged predictions. Missing legacy
   * entries may initialize once; a learned-list notification must not call this. */
  hydrateLegacy(charges: Record<string, unknown>): void {
    if (this.lifeId === 0) this.hydrate(charges);
  }

  private hydrate(charges: Record<string, unknown>): void {
    for (const [id, config] of Object.entries(ABILITY_CHARGE_CONFIGS)) {
      const raw = charges[id];
      const state = raw && typeof raw === 'object' ? {
        ...raw, rechargeStartedAt: (raw as ChargeWireState).rechargeStartedAt ?? null,
        lastCastAt: (raw as ChargeWireState).lastCastAt ?? null,
      } : raw;
      if (validState(state, config.maxCharges)) this.sync(id, state);
      else this.base[id] ??= createInitialChargeState(config);
    }
  }

  applySnapshot(message: AbilityChargeSnapshot): boolean {
    if (!validSnapshot(message) || message.sessionId !== this.sessionId
      || (this.characterId && message.characterId && message.characterId !== this.characterId)
      || message.lifeId < this.lifeId
      || (message.lifeId === this.lifeId && message.revision <= this.revision)) return false;
    // First owner hydration may race a local cast. Preserve that prediction.
    // A later actor replacement is a different life and retires the old work.
    if (this.lifeId !== 0 && message.lifeId !== this.lifeId) {
      this.pending.clear();
      this.base = {};
    }
    this.characterId ||= message.characterId;
    this.lifeId = message.lifeId;
    this.revision = message.revision;
    for (const sequence of message.processedCastSeqs) this.pending.delete(sequence);
    this.hydrate(message.charges);
    return true;
  }
}

function validState(value: unknown, capacity: number): value is ChargeWireState {
  if (!value || typeof value !== 'object') return false;
  const state = value as ChargeWireState;
  return Number.isInteger(state.currentCharges) && state.currentCharges >= 0 && state.currentCharges <= capacity
    && (state.rechargeStartedAt === null || Number.isFinite(state.rechargeStartedAt))
    && (state.lastCastAt === null || Number.isFinite(state.lastCastAt));
}

function validSnapshot(value: AbilityChargeSnapshot): boolean {
  if (!value || value.version !== 1 || typeof value.sessionId !== 'string' || typeof value.characterId !== 'string'
    || !Number.isSafeInteger(value.lifeId) || value.lifeId <= 0 || !Number.isSafeInteger(value.revision) || value.revision <= 0
    || !Array.isArray(value.processedCastSeqs) || value.processedCastSeqs.length > 256
    || !value.processedCastSeqs.every(seq => Number.isSafeInteger(seq) && seq > 0)
    || !value.charges || typeof value.charges !== 'object' || Array.isArray(value.charges)) return false;
  return Object.entries(value.charges).every(([id, state]) => {
    const config = abilityChargeConfig(id);
    return !!config && validState(state, config.maxCharges);
  });
}
